import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { listRoles } from '../../services/api';
import type { RoleResponse } from '../../services/api';
import { filterUserVisibleRoles } from '../../utils/rbacRoles';
import {
  DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS,
  type OnboardingTeamMemberDraft,
} from './onboardingTeamMember';

type Props = {
  open: boolean;
  member: OnboardingTeamMemberDraft | null;
  onClose: () => void;
  onSave: (member: OnboardingTeamMemberDraft) => void;
};

export const OnboardingTeamMemberRolesDialog: React.FC<Props> = ({
  open,
  member,
  onClose,
  onSave,
}) => {
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<RoleResponse[]>([]);
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);

  useEffect(() => {
    if (!open || !member) return;

    setSelectedSlugs(member.role_slugs);
    setLoadingRoles(true);
    listRoles({})
      .then(res => {
        const roles = res.data ?? [];
        setAvailableRoles(
          filterUserVisibleRoles(
            roles.filter(role => role.scope !== 'clinic')
          ).sort(
            (a, b) =>
              Number(a.is_internal ?? false) - Number(b.is_internal ?? false)
          )
        );
      })
      .catch(error => {
        console.error('Failed to load roles for onboarding team:', error);
        setAvailableRoles([]);
      })
      .finally(() => setLoadingRoles(false));
  }, [open, member]);

  const toggleSlug = (slug: string) => {
    setSelectedSlugs(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const handleSave = () => {
    if (!member) return;
    onSave({
      email: member.email,
      role_slugs:
        selectedSlugs.length > 0
          ? selectedSlugs
          : [...DEFAULT_ONBOARDING_TEAM_ROLE_SLUGS],
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Auto-assign roles</DialogTitle>
        </DialogHeader>

        {member && (
          <p className='text-sm text-muted-foreground truncate'>
            {member.email}
          </p>
        )}

        {loadingRoles ? (
          <div className='flex items-center gap-2 py-6 text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading roles…
          </div>
        ) : (
          <div className='max-h-72 space-y-2 overflow-y-auto py-2'>
            {availableRoles.map(role => {
              const slug = role.slug ?? '';
              if (!slug) return null;
              const inputId = `onboarding-role-${slug}`;
              return (
                <div key={slug} className='flex items-start gap-2'>
                  <Checkbox
                    id={inputId}
                    checked={selectedSlugs.includes(slug)}
                    onCheckedChange={() => toggleSlug(slug)}
                  />
                  <Label
                    htmlFor={inputId}
                    className='cursor-pointer leading-snug'
                  >
                    <span className='font-medium'>{role.name}</span>
                    <span className='block text-xs text-muted-foreground'>
                      {slug}
                    </span>
                  </Label>
                </div>
              );
            })}
            {availableRoles.length === 0 && (
              <p className='text-sm text-muted-foreground'>
                No roles available.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loadingRoles || !member}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTeamMemberRolesDialog;
